import React from "react";
import { WidgetWrapper } from "../../../../components/WidgetWrapper/WidgetWrapper";
import { EditField } from "../../../../components/EditPanel/types";
import { useWidgetEditable } from "../../../../hooks/useWidgetEditable";
import { themeColor, customColor } from "../../../../types/color";
import {
  ConfirmActionDialogWidgetProps,
  ConfirmActionDialogWidgetState,
} from "./types";

const defaultState: ConfirmActionDialogWidgetState = {
  overlayBgColor: customColor("rgba(0, 0, 0, 0.45)"),
  dialogBgColor: themeColor("secondary"),
  dialogBorderRadius: 12,
  titleColor: themeColor("textPrimary"),
  messageColor: themeColor("textPrimary"),
  cancelButtonBgColor: themeColor("secondary"),
  cancelButtonBorderColor: themeColor("borderColor"),
  cancelButtonTextColor: themeColor("textPrimary"),
  confirmButtonBgColor: themeColor("primary"),
  confirmButtonTextColor: themeColor("primaryForeground"),
};

const editFields: EditField[] = [
  { key: "overlayBgColor", label: "Overlay Background", type: "color" },
  { key: "dialogBgColor", label: "Dialog Background", type: "color" },
  {
    key: "dialogBorderRadius",
    label: "Dialog Border Radius",
    type: "number",
    min: 0,
    max: 24,
    step: 1,
  },
  { key: "titleColor", label: "Title Color", type: "color" },
  { key: "messageColor", label: "Message Color", type: "color" },
  {
    key: "cancelButtonBgColor",
    label: "Cancel Button Background",
    type: "color",
  },
  {
    key: "cancelButtonBorderColor",
    label: "Cancel Button Border",
    type: "color",
  },
  {
    key: "cancelButtonTextColor",
    label: "Cancel Button Text",
    type: "color",
  },
  {
    key: "confirmButtonBgColor",
    label: "Confirm Button Background",
    type: "color",
  },
  {
    key: "confirmButtonTextColor",
    label: "Confirm Button Text",
    type: "color",
  },
];

export const ConfirmActionDialogWidget: React.FC<
  ConfirmActionDialogWidgetProps
> = ({ initialState = {}, widgetId, displayName, overlay, children }) => {
  const {
    state,
    isLoading,
    isSaving,
    handleEditChange,
    handleSave,
    themeColors,
    resolveColor,
    canSave,
  } = useWidgetEditable<ConfirmActionDialogWidgetState>(
    widgetId,
    { ...defaultState, ...initialState },
    editFields
  );

  if (isLoading) {
    return <></>;
  }

  const overlayContent = overlay ? overlay(state, resolveColor) : null;
  const widgetContent = children(state, resolveColor);

  return (
    <>
      {overlayContent}
      <WidgetWrapper
        widgetId={widgetId}
        displayName={displayName}
        editFields={editFields}
        editValues={state}
        onEditChange={handleEditChange}
        onSave={canSave ? handleSave : undefined}
        isSaving={isSaving}
        themeColors={themeColors}
        triggerMode="persistent"
        triggerPosition="top-right"
        wrapperStyle={{ width: "100%", maxWidth: "28rem" }}
      >
        {widgetContent}
      </WidgetWrapper>
    </>
  );
};
